import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import SeeMore from '~shared/ui/SeeMore';
import { useSortedSelectionsIds } from '~SpectateStore/hooks/selectionsHooks';
import {
    useHomeTeamName,
    useAwayTeamName,
} from '~SpectateStore/hooks/eventsHooks';
import CellSelectionSimple from './CellSelectionSimple';
import './styles/gridScores.scss';

const DEFAULT_EXPANDED_ROWS = 5;

const SelectionsGridScores = ({ eventId, marketId }) => {
    const selectionsIds = useSortedSelectionsIds(eventId, marketId);
    const [defaultExpandedCells, setDefaultExpandedCells] = useState([]);
    const [defaultCollapsedCells, setDefaultCollapsedCells] = useState([]);
    const [isThreeWay, setIsThreeWay] = useState(true);
    const scoresGridClass = classNames('scores-grid', {
        'scores-grid--three-way': isThreeWay,
        'scores-grid--two-way': !isThreeWay,
    });
    const [ t ] = useTranslation();
    const teamNames = {
        home: useHomeTeamName(eventId) ?? t('sub_mkt_headers.home'),
        draw: t('sub_mkt_headers.draw'),
        away: useAwayTeamName(eventId) ?? t('sub_mkt_headers.away'),
    };
    const scoreKeys = isThreeWay ? ['home', 'draw', 'away'] : ['home', 'away'];

    useEffect(() => {
        const { home = [], draw = [], away = [] } = selectionsIds;
        if ((home.length !== 0) && (away.length !== 0) && (draw.length === 0)) {
            setIsThreeWay(false);
        }

        setGridCellComponents();
    }, []);

    useEffect(() => {
        setGridCellComponents();
    }, [selectionsIds.home.length, selectionsIds.draw.length, selectionsIds.away.length, isThreeWay]);

    const setGridCellComponents = () => {
        const { home = [], draw = [], away = [] } = selectionsIds;
        const expanded = [];
        const collapsed = [];
        const numberOfRows = Math.max(home.length, draw.length, away.length);

        for (let i = 0; i < numberOfRows; i++) {
            scoreKeys.forEach(
                key => {
                    const id = selectionsIds[key][i] ?? null;
                    const cellClass = classNames(`scores-grid__${key}`, {
                        'scores-grid__empty': !id,
                        'last-row': (i < DEFAULT_EXPANDED_ROWS) && (i === numberOfRows - 1),
                    });

                    let cell = (!id)
                    ? (
                        <div
                            key={`${key}-row${i}`}
                            className={cellClass}
                        />
                    )
                    : (
                        <div
                            key={`${key}-${id}`}
                            className={cellClass}
                        >
                            <CellSelectionSimple
                                key={`selection-${id}`}
                                id={id}
                            />
                        </div>
                    );

                    if (i < DEFAULT_EXPANDED_ROWS) {
                        expanded.push(cell);
                    } else {
                        collapsed.push(cell);
                    }
                }
            );
        }

        setDefaultExpandedCells(expanded);
        setDefaultCollapsedCells(collapsed);
    };

    const headers = () => {
        return scoreKeys.map(
            key => (
                <div key={`header-${key}`}>
                    {teamNames[key].toUpperCase()}
                </div>
            )
        );
    };

    return (
        <>
            <div className={`scores-grid__header ${scoresGridClass}`}>
                {headers()}
            </div>
            <div className={scoresGridClass}>
                {defaultExpandedCells}
            </div>
            {
                (defaultCollapsedCells.length !== 0)
                ? <SeeMore
                        selections={[
                            <div
                                key={'collapsed-grid'}
                                className={scoresGridClass}
                            >
                                {defaultCollapsedCells}
                            </div>,
                        ]}
                        collapseExtraClass="scores-grid__see-more"
                    />
                : null
            }
        </>
    );
};

SelectionsGridScores.propTypes = {
    eventId: PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.number.isRequired,
    ]),
    marketId: PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.number.isRequired,
    ]),
};

export default SelectionsGridScores;
